import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Chart, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { DARK_BASE, TEAL, SEV_COLORS } from './constants';
import ChartCard from './ChartCard';
import { useChartTheme } from './useChartTheme';

Chart.register(Tooltip, Legend, BarElement, LinearScale, CategoryScale);

const TYPE_COLORS = [TEAL, DARK_BASE, SEV_COLORS.High, '#C00065', '#179F8E', '#353951'];

interface ProjectTypesChartProps {
  /** Map of project type → count */
  data: Record<string, number>;
  height?: number;
  /** When provided, clicking any bar navigates to this path */
  reportPath?: string | null;
}

const ProjectTypesChart: React.FC<ProjectTypesChartProps> = ({ data, height = 220, reportPath }) => {
  const { textColor } = useChartTheme();
  const navigate = useNavigate();

  const labels = Object.keys(data);
  const values = Object.values(data);

  const chartData = {
    labels,
    datasets: [
      {
        backgroundColor: TYPE_COLORS,
        data: values,
        borderRadius: 2,
        maxBarThickness: 32,
      },
    ],
  };

  const options = {
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { color: textColor, font: { size: 11 } } },
      y: { grid: { display: false }, ticks: { color: textColor, stepSize: 1, font: { size: 11 } } },
    },
    responsive: true,
    maintainAspectRatio: false,
    onClick: reportPath
      ? (_: any, elements: any[]) => { if (elements.length > 0) navigate(reportPath); }
      : undefined,
    onHover: reportPath
      ? (event: any, elements: any[]) => {
          const canvas = event.native?.target as HTMLCanvasElement | null;
          if (canvas) canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        }
      : undefined,
  } as any;

  return (
    <ChartCard title="Project types" sub="Findings by project category">
      {labels.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div style={{ flex: 1 }} />
          <div style={{ height }}>
            <Bar data={chartData} options={options} />
          </div>
        </div>
      ) : (
        <div className="text-muted text-center py-5 small">No data</div>
      )}
    </ChartCard>
  );
};

export default ProjectTypesChart;
